import { h, setChildren } from "../core/dom.js";
import { profile, loadProfile } from "../store.js";
import { icon } from "../lib/icons.js";
import { backLink } from "../components/common.js";
import { spinner, emptyState, errorState, toast, openModal, confirmDialog } from "../components/ui.js";
import { openCamera } from "../components/cameraCapture.js";
import { addPhoto, listPhotos, deletePhoto } from "../lib/photoStore.js";

// Photos carry a numeric epoch (created_at), not a server datetime string, so format locally.
const fmtDate = (ms) => new Date(ms).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });

export default function Progress() {
  const root = h("div", { class: "screen" });
  let photos = [];
  let urls = []; // object URLs created this render, revoked on the next

  load();

  function revokeUrls() {
    urls.forEach((u) => URL.revokeObjectURL(u));
    urls = [];
  }
  function urlFor(blob) {
    const u = URL.createObjectURL(blob);
    urls.push(u);
    return u;
  }

  async function load() {
    setChildren(root, backLink("Progress", "/history"), spinner());
    try {
      if (!profile()) await loadProfile();
      photos = await listPhotos(profile().id);
      render();
    } catch (e) {
      setChildren(root, backLink("Progress", "/history"), errorState(e.message || "Couldn't load photos", load));
    }
  }

  function render() {
    revokeUrls();
    setChildren(root,
      backLink("Progress", "/history"),
      h("div", { class: "row-between", style: { alignItems: "center" } },
        h("h1", { style: { margin: "4px 0" } }, "Progress photos"),
        photos.length >= 2
          ? h("button", { class: "btn btn-ghost", style: { padding: "6px 12px" }, onClick: openCompare }, "Compare")
          : null
      ),
      h("p", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } },
        "Stored only on this device. The camera ghosts your last photo so you can line up the same pose each time."),

      h("button", { class: "btn btn-primary btn-block btn-lg mt", onClick: takePhoto }, icon("camera", { size: 20 }), "Take progress photo"),

      photos.length
        ? h("div", { class: "photo-grid mt-lg" }, ...photos.map(thumb))
        : emptyState({ icon: "camera", title: "No photos yet", message: "Snap your first progress photo. Future shots overlay this one so your angle and framing stay consistent." })
    );
  }

  function thumb(p) {
    return h("button", { class: "photo-cell", onClick: () => viewPhoto(p) },
      h("img", { src: urlFor(p.blob), alt: `Progress photo ${fmtDate(p.created_at)}`, loading: "lazy" }),
      h("span", { class: "photo-date" }, fmtDate(p.created_at))
    );
  }

  function takePhoto() {
    const ghostUrl = photos.length ? urlFor(photos[0].blob) : null; // most recent as the alignment guide
    openCamera({
      ghostUrl,
      onCapture: async (blob) => {
        try {
          await addPhoto(profile().id, blob, { type: blob.type || "image/jpeg" });
          toast("Photo saved", "success");
          await load();
        } catch (e) {
          toast(e?.name === "QuotaExceededError" ? "This device is out of storage" : "Couldn't save photo", "error");
        }
      },
    });
  }

  function viewPhoto(p) {
    const m = openModal({
      title: fmtDate(p.created_at),
      fullscreen: true,
      content: h("div", { class: "photo-view" },
        h("img", { src: urlFor(p.blob), alt: "Progress photo" }),
        h("button", { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" },
          onClick: async () => {
            const ok = await confirmDialog({ title: "Delete photo?", message: `Remove the photo from ${fmtDate(p.created_at)}? This can't be undone.`, confirmText: "Delete", danger: true });
            if (!ok) return;
            try { await deletePhoto(p.id); m.close(); toast("Photo deleted", "success"); await load(); }
            catch { toast("Couldn't delete", "error"); }
          } }, icon("trash", { size: 18 }), "Delete photo")
      ),
    });
  }

  function openCompare() {
    const newest = photos[0];
    const oldest = photos[photos.length - 1];
    openModal({
      title: "Then & now",
      fullscreen: true,
      content: h("div", null,
        h("div", { class: "photo-compare" }, compareSide(oldest, "First"), compareSide(newest, "Latest")),
        h("p", { class: "dim center mt", style: { fontSize: ".8rem" } },
          `${fmtDate(oldest.created_at)} → ${fmtDate(newest.created_at)}`)
      ),
    });
  }

  function compareSide(p, label) {
    return h("div", { class: "photo-compare-side" },
      h("img", { src: urlFor(p.blob), alt: `${label} progress photo` }),
      h("span", { class: "photo-compare-label" }, label)
    );
  }

  return root;
}
